import { derived } from 'svelte/store'
import { page } from '@sveltech/routify'
import { authStore } from 'totalinvoice-frontend-core'

export const isLoggedIn = derived(authStore, $authStore => $authStore.authenticated)
export const isCustomer = derived(authStore, $authStore => $authStore.roles && $authStore.roles.includes("customer"))
export const isDealer = derived(authStore, $authStore => $authStore.roles && $authStore.roles.includes("dealer"))
export const canManageUsers = derived(authStore, $authStore => $authStore.roles && $authStore.roles.includes("manage_users"))
export const canManageTasks = derived(authStore, $authStore => $authStore.roles && $authStore.roles.includes("manage_tasks"))
export const canManageDealers = derived(authStore, $authStore => $authStore.roles && $authStore.roles.includes("manage_dealers"))
export const canManageCustomers = derived(authStore, $authStore => $authStore.roles && $authStore.roles.includes("manage_customers"))
export const canShowSystemStatus = derived(authStore, $authStore => $authStore.roles && $authStore.roles.includes("show_system_status"))
export const canShowStatistics = derived(authStore, $authStore => $authStore.roles && $authStore.roles.includes("show_statistics"))
export const canSendEmail = derived(authStore, $authStore => $authStore.roles && $authStore.roles.includes("send_email"))

class Permissions {
    constructor(
        isLoggedIn, isCustomer, isDealer, canManageUsers, canManageTasks, canManageDealers,
        canManageCustomers, canShowSystemStatus, canShowStatistics, canSendEmail
    ) {
        this.isLoggedIn = isLoggedIn
        this.isCustomer = isCustomer
        this.isDealer = isDealer
        this.canManageUsers = canManageUsers
        this.canManageTasks = canManageTasks
        this.canManageDealers = canManageDealers
        this.canManageCustomers = canManageCustomers
        this.canShowSystemStatus = canShowSystemStatus
        this.canShowStatistics = canShowStatistics
        this.canSendEmail = canSendEmail
    }
}

/**
 * Observe user permissions.
 */
export const permissions = derived(
    [
        isLoggedIn, isCustomer, isDealer, canManageUsers, canManageTasks, canManageDealers,
        canManageCustomers, canShowSystemStatus, canShowStatistics, canSendEmail
    ],
    ([
        $isLoggedIn, $isCustomer, $isDealer, $canManageUsers, $canManageTasks, $canManageDealers,
        $canManageCustomers, $canShowSystemStatus, $canShowStatistics, $canSendEmail
    ]) => {
        return new Permissions(
            $isLoggedIn, $isCustomer, $isDealer, $canManageUsers, $canManageTasks, $canManageDealers,
            $canManageCustomers, $canShowSystemStatus, $canShowStatistics, $canSendEmail
        )
    }
)

/**
 * Observe if the user is allowed to access the current page.
 */
export const canActivate = derived([page, permissions], ([$page, $permissions]) => canActivatePage($page, $permissions))

/**
 * Check if the user is allowed to access the given page.
 * 
 * @param {ClientNodeApi} page
 * @param {Permissions} permissions
 */
export function canActivatePage(page, permissions) {
    const guards = getAllGuardsOfPage(page)
    for (let guard of guards) {
        if (!permissions[guard]) {
            return false
        }
    }
    return true
}

/**
 * Retrieves all guards of the current page.
 * 
 * Guards can be defined using Routify's meta data:
 * <!-- routify:options guards=['guardName1', 'guardName2']-->
 * 
 * The guards are sorted in the order they appear concerning the page's route.
 * Duplicates are removed.
 * 
 * Example: /foo/bar/page.svelte
 * 1. guards of /_layout.svelte
 * 2. guards of /foo/_layout.svelte
 * 3. guards of /foo/bar/_layout.svelte
 * 4. guards of /foo/bar/page.svelte
 * 
 * @param {ClientNodeApi} page
 * @returns {Set<string>}
 */
function getAllGuardsOfPage(page) {
    let guards = []
    while (page) {
        if (page.meta['guards']) guards.unshift(...page.meta['guards'])
        page = page.parent
    }
    return new Set(guards)
}
